import React, { useState, useEffect } from 'react';
import { Film, Upload, HardDrive, FileVideo, RefreshCw, Layers } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import VideoUploadModal from '@/components/admin/VideoUploadModal';
import BulkVideoUploadModal from '@/components/admin/BulkVideoUploadModal';
import VideoManagementTable from '@/components/admin/VideoManagementTable';
import { adminVideoService, VideoListItem } from '@/services/adminVideoService';

export default function AdminVideos() {
  const [videos, setVideos] = useState<VideoListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [stats, setStats] = useState({
    totalVideos: 0,
    totalSizeGb: 0,
    averageSizeMb: 0,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminVideoService.listVideos();
      setVideos(response.videos);
      setStats({
        totalVideos: response.total_count,
        totalSizeGb: response.total_size_gb,
        averageSizeMb: response.total_count > 0
          ? (response.total_size_gb * 1024) / response.total_count
          : 0,
      });
    } catch (error: any) {
      console.error('Error fetching videos:', error);
      setError(error.message || 'Failed to fetch videos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (courseId: string, topicId: string) => {
    try {
      await adminVideoService.deleteVideo({ courseId, topicId });
      await fetchVideos(); // Refresh the list
    } catch (error: any) {
      console.error('Error deleting video:', error);
      setError(error.message || 'Failed to delete video');
      throw error; // Re-throw to handle in the table component
    }
  };

  const handleUploadSuccess = () => {
    fetchVideos(); // Refresh the list after successful upload
  };

  const formatStorageSize = (sizeGb: number) => {
    if (sizeGb < 1) {
      return `${(sizeGb * 1024).toFixed(2)} MB`;
    }
    return `${sizeGb.toFixed(2)} GB`;
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-purple-900/20 to-blue-900/20 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Video Management</h1>
              <p className="text-gray-400">
                Upload, manage, and organize course videos
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={fetchVideos}
                disabled={isLoading}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg transition-all flex items-center gap-2 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={() => setShowBulkUploadModal(true)}
                className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg transition-all flex items-center gap-2 shadow-lg"
              >
                <Layers className="w-4 h-4" />
                Bulk Upload
              </button>
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-all flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload Video
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Total Videos */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl blur-xl opacity-20 group-hover:opacity-30 transition-all"></div>
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/20 hover:border-white/30 transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-purple-600/20 rounded-lg">
                    <FileVideo className="w-6 h-6 text-purple-400" />
                  </div>
                  <span className="text-2xl font-bold text-white">
                    {stats.totalVideos}
                  </span>
                </div>
                <p className="text-gray-400 text-sm">Total Videos</p>
              </div>
            </div>

            {/* Total Storage */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl blur-xl opacity-20 group-hover:opacity-30 transition-all"></div>
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/20 hover:border-white/30 transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-blue-600/20 rounded-lg">
                    <HardDrive className="w-6 h-6 text-blue-400" />
                  </div>
                  <span className="text-2xl font-bold text-white">
                    {formatStorageSize(stats.totalSizeGb)}
                  </span>
                </div>
                <p className="text-gray-400 text-sm">Total Storage Used</p>
              </div>
            </div>

            {/* Average Size */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-green-600 rounded-xl blur-xl opacity-20 group-hover:opacity-30 transition-all"></div>
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/20 hover:border-white/30 transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-cyan-600/20 rounded-lg">
                    <Film className="w-6 h-6 text-cyan-400" />
                  </div>
                  <span className="text-2xl font-bold text-white">
                    {stats.averageSizeMb.toFixed(1)} MB
                  </span>
                </div>
                <p className="text-gray-400 text-sm">Average Video Size</p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {/* Video Table */}
          <div className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 backdrop-blur-xl rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Uploaded Videos</h2>
              <p className="text-gray-400 text-sm">
                {videos.length} {videos.length === 1 ? 'video' : 'videos'} found
              </p>
            </div>

            <VideoManagementTable
              videos={videos}
              isLoading={isLoading}
              onDelete={handleDelete}
              onRefresh={fetchVideos}
            />
          </div>

          {/* Storage Information */}
          <div className="mt-8 p-4 bg-gradient-to-br from-white/5 to-white/0 rounded-lg border border-white/10">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-yellow-600/20 rounded-lg">
                <HardDrive className="w-4 h-4 text-yellow-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-yellow-400 mb-1">
                  Storage Information
                </h3>
                <p className="text-xs text-gray-400">
                  Videos are stored in AWS S3. Current usage: {formatStorageSize(stats.totalSizeGb)}
                  {' '}(Estimated monthly cost: ${(stats.totalSizeGb * 0.023).toFixed(2)})
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Maximum file size per video: 2GB • Supported formats: MP4, WebM, MOV, AVI
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      <VideoUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={handleUploadSuccess}
      />

      {/* Bulk Upload Modal */}
      <BulkVideoUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onUploadComplete={handleUploadSuccess}
      />
    </AdminLayout>
  );
}